import { FC } from "react";

import { useTranslation } from "react-i18next";

import { useAmplitude } from "@hooks/amplitude/useAmplitude";

import { getNavigationLinkProperties } from "./NavigationItem.utilities";

import { alpha } from "@mui/material/styles";
import ListItem from "@mui/material/ListItem";
import ListItemButton, { listItemButtonClasses } from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";

import { brandColorTokens, brandRadius } from "@constants";
import { navigationItemDimensions } from "../../NavigationList.constants";

import { INavigationItemProperties } from "./NavigationItem.types";

import { activeIconStyle } from "./NavigationItem.styles";

const hoverBackground = alpha(brandColorTokens.text.bodyInverted, 0.1);

const NavigationItem: FC<INavigationItemProperties> = ({ item, isActive, isCollapsed, onNavigate }) => {
  const { t } = useTranslation();
  const { track } = useAmplitude();

  const Icon = item.icon;
  const label = t(`navigation.${item.itemKey}`);

  const handleNavigationItemSelect = () => {
    track(`drawer.listItemButton.${item.itemKey}`);
    onNavigate?.();
  };

  return (
    <ListItem
      disablePadding={true}
      sx={{
        px: `${navigationItemDimensions.horizontalInset}px`,
        py: `${navigationItemDimensions.verticalSpacing}px`,
      }}
    >
      <Tooltip title={isCollapsed ? label : ""} placement="right">
        <ListItemButton
          {...getNavigationLinkProperties(item)}
          selected={isActive}
          aria-current={isActive ? "page" : undefined}
          onClick={handleNavigationItemSelect}
          data-cy={`app-layout-navigation-${item.itemKey}`}
          sx={{
            position: "relative",
            minHeight: navigationItemDimensions.height,
            px: 1.5,
            borderRadius: `${brandRadius.medium}px`,
            color: "inherit",
            "&:hover": { bgcolor: hoverBackground },
            [`&.${listItemButtonClasses.selected}, &.${listItemButtonClasses.selected}:hover`]: {
              bgcolor: "transparent",
              color: brandColorTokens.text.display,
              fontWeight: 500,
            },
            [`&.${listItemButtonClasses.focusVisible}`]: { bgcolor: hoverBackground },
          }}
        >
          <ListItemIcon sx={[{ position: "relative", minWidth: 40, color: "inherit" }, isActive && activeIconStyle]}>
            <Icon />
          </ListItemIcon>
          <ListItemText
            primary={label}
            slotProps={{ primary: { fontWeight: "inherit", noWrap: true } }}
            sx={{ position: "relative", opacity: isCollapsed ? 0 : 1, transition: "opacity 0.2s" }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default NavigationItem;
